import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { FileText, ExternalLink, CreditCard, Loader2, Download, Receipt } from 'lucide-react';
import { openBillingPortal, listInvoices } from '../lib/payments';

function formatMoney(amount, currency = 'usd') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format((amount || 0) / 100);
}

function formatDate(unix) {
  return new Date(unix * 1000).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

function StatusBadge({ status }) {
  const color = { paid: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
                  open: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
                  void: 'bg-zinc-700/40 text-zinc-500 border-zinc-600/40',
                  uncollectible: 'bg-red-500/15 text-red-400 border-red-500/30' }[status]
                || 'bg-zinc-700/40 text-zinc-500 border-zinc-600/40';
  return <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-1 ${color}`}>{status || 'unknown'}</span>;
}

export default function Billing() {
  const [email, setEmail] = React.useState(() => localStorage.getItem('am_billing_email') || '');
  const [loadingPortal, setLoadingPortal] = React.useState(false);
  const [loadingInvoices, setLoadingInvoices] = React.useState(false);
  const [invoices, setInvoices] = React.useState(null);

  const load = async (e) => {
    if (e) e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      return toast.error('Enter the email you used to subscribe');
    }
    localStorage.setItem('am_billing_email', email);
    try {
      setLoadingInvoices(true);
      const { invoices: list } = await listInvoices(email);
      setInvoices(list);
      if (list.length === 0) toast.info('No invoices found for this email yet');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load invoices');
    } finally {
      setLoadingInvoices(false);
    }
  };

  const openPortal = async () => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) return toast.error('Enter a valid email first');
    try {
      setLoadingPortal(true);
      const { portal_url } = await openBillingPortal(email);
      window.location.href = portal_url;
    } catch (err) {
      setLoadingPortal(false);
      toast.error(err?.response?.data?.detail || 'Could not open billing portal');
    }
  };

  const input = 'w-full bg-[#0d0d0d] hairline px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#ff2a2a]';

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-14" data-testid="page-billing">
      <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-2">Billing</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight leading-none">Invoices &amp; subscriptions</h1>
      <p className="mt-3 text-zinc-400 text-sm max-w-lg">
        Manage your plan, download receipts, update payment methods, and cancel anytime.
      </p>

      <div className="mt-8 am-card p-6" data-testid="billing-lookup">
        <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">Look up your account</div>
        <form onSubmit={load} className="flex flex-col md:flex-row gap-3">
          <input
            data-testid="billing-email"
            className={input + ' md:flex-1'}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email used at checkout"
          />
          <button
            type="submit"
            disabled={loadingInvoices}
            data-testid="billing-load-invoices"
            className="hairline hover:border-[#ff2a2a] text-white text-sm font-semibold px-5 py-2.5 inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {loadingInvoices ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            View invoices
          </button>
          <button
            type="button"
            onClick={openPortal}
            disabled={loadingPortal}
            data-testid="billing-open-portal"
            className="bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-5 py-2.5 inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {loadingPortal ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            Manage subscription
          </button>
        </form>
        <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600">
          Powered by Stripe · secure and PCI-DSS compliant
        </div>
      </div>

      {invoices !== null && (
        <div className="mt-8" data-testid="invoice-list">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl uppercase tracking-tight">Recent invoices</h2>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              {invoices.length} {invoices.length === 1 ? 'invoice' : 'invoices'}
            </div>
          </div>

          {invoices.length === 0 ? (
            <div className="am-card p-10 text-center" data-testid="invoice-empty">
              <Receipt className="w-10 h-10 mx-auto text-zinc-600" />
              <div className="mt-3 text-white font-semibold">No invoices yet</div>
              <p className="mt-2 text-zinc-400 text-sm">
                Once you subscribe to a plan or buy transport insurance, invoices show up here.
              </p>
              <Link to="/pricing" className="mt-4 inline-block bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-5 py-2.5 transition-colors">
                View plans
              </Link>
            </div>
          ) : (
            <div className="am-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="hairline-b">
                    <th className="text-left font-mono uppercase tracking-widest text-xs text-zinc-500 p-4">Invoice</th>
                    <th className="text-left font-mono uppercase tracking-widest text-xs text-zinc-500 p-4">Date</th>
                    <th className="text-left font-mono uppercase tracking-widest text-xs text-zinc-500 p-4">Description</th>
                    <th className="text-right font-mono uppercase tracking-widest text-xs text-zinc-500 p-4">Amount</th>
                    <th className="text-center font-mono uppercase tracking-widest text-xs text-zinc-500 p-4">Status</th>
                    <th className="text-right font-mono uppercase tracking-widest text-xs text-zinc-500 p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="hairline-b last:border-b-0" data-testid={`invoice-row-${inv.id}`}>
                      <td className="p-4 font-mono text-xs text-white">{inv.number || inv.id.slice(-8)}</td>
                      <td className="p-4 text-zinc-300">{formatDate(inv.created)}</td>
                      <td className="p-4 text-zinc-300 truncate max-w-xs" title={inv.description}>{inv.description || '—'}</td>
                      <td className="p-4 text-right text-white font-semibold">{formatMoney(inv.amount_paid, inv.currency)}</td>
                      <td className="p-4 text-center"><StatusBadge status={inv.status} /></td>
                      <td className="p-4 text-right">
                        <div className="inline-flex gap-2">
                          {inv.hosted_invoice_url && (
                            <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" title="View invoice"
                              className="p-2 text-zinc-400 hover:text-[#ff2a2a] transition-colors" data-testid={`invoice-view-${inv.id}`}>
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          {inv.invoice_pdf && (
                            <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" title="Download PDF"
                              className="p-2 text-zinc-400 hover:text-[#ff2a2a] transition-colors" data-testid={`invoice-pdf-${inv.id}`}>
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="mt-16 am-card p-6" data-testid="billing-quick-links">
        <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">Quick links</div>
        <div className="grid md:grid-cols-3 gap-3 text-sm">
          <Link to="/pricing" className="hairline hover:border-[#ff2a2a] px-4 py-3 text-white transition-colors">Change plan →</Link>
          <Link to="/maps" className="hairline hover:border-[#ff2a2a] px-4 py-3 text-white transition-colors">Maps &amp; amenities →</Link>
          <a href="mailto:billing@automobile.direct" className="hairline hover:border-[#ff2a2a] px-4 py-3 text-white transition-colors">Contact billing support →</a>
        </div>
      </div>
    </div>
  );
}
